import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { AISummaryPanel } from '@/components/shared/AISummaryPanel';
import { ArrowLeft, Plus, DollarSign, Zap, User, FileText, Clock, Calendar, AlertTriangle, RefreshCw, Users, Printer, MapPin, Shield, UserCheck, Download, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDialog } from '@/hooks/useAppDialog';
// Participant Module Upgrade - Extended Tabs
import { AddressesTab, SupportTeamTab, PreferencesRisksTab, NDISFundingDetailsTab, ConsentsTab, ExpiringItemsTab, DuplicateDetectionTab, LinkedModulesTab, OverviewSummaryCards, GoalsExtendedTab, TagsTab, FlagsTab, TimelineEventsTab } from './ParticipantExtendedTabs';
import { ParticipantChartsTab } from './charts/ParticipantChartsTab';
import { ParticipantDocumentsTab } from './documents/ParticipantDocumentsTab';
import { SupportSnapshotCard } from '@/components/participants/support-snapshot/SupportSnapshotCard';
import { ParticipantHeaderV2 } from '@/components/participants/profile/ParticipantHeaderV2';
import { ParticipantOverviewV2 } from '@/components/participants/profile/overview/ParticipantOverviewV2';
import { ParticipantUnifiedTimeline } from '@/components/participants/timeline/ParticipantUnifiedTimeline';
import { QuickActionBar } from '@/components/participants/profile/QuickActionBar';
import { CompactKpiRow } from '@/components/participants/profile/CompactKpiRow';
import { InlineTimelinePreview } from '@/components/participants/profile/InlineTimelinePreview';
import { ParticipantProfileV3Layout } from '@/components/participants/profile/ParticipantProfileV3Layout';
import { useAuth } from '@/lib/auth';

const dayNames = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

// Define ClipboardCheck component before using it
const ClipboardCheck = ({ className }) => <FileText className={className} />;

const TIMELINE_ICONS = {
  funding_plan: DollarSign, funding_transaction: DollarSign,
  shift: Calendar, shift_cancellation: AlertTriangle,
  timesheet: ClipboardCheck, invoice: FileText,
  progress_note: FileText, claim_batch: DollarSign, claim_line: DollarSign,
  audit: Clock, incident: AlertTriangle,
};

import { Badge } from '@/components/ui/badge';

function ContinuityTab({ participantId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/roster/intelligence/participants/${participantId}/continuity?days=90`)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  if (loading) return <p className="text-muted-foreground py-4">Loading continuity data...</p>;
  if (!data) return <p className="text-muted-foreground py-4">No continuity data available</p>;

  const levelColor = data.continuityLevel === 'GOOD' ? 'text-green-600 bg-green-50 border-green-200' : data.continuityLevel === 'MODERATE' ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';

  return (
    <div className="space-y-4" data-testid="continuity-tab">
      {/* Score Card */}
      <Card className={`border ${levelColor.split(' ').slice(2).join(' ')}`}>
        <CardContent className="py-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Continuity Score (last {data.periodDays} days)</p>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-4xl font-bold ${levelColor.split(' ')[0]}`}>{data.continuityScore}%</span>
                <Badge className={levelColor.split(' ').slice(0, 2).join(' ')}>{data.continuityLevel}</Badge>
              </div>
            </div>
            <div className="text-right text-sm">
              <p>{data.totalShifts} total shifts</p>
              <p className="text-muted-foreground">{data.uniqueStaffCount} different staff</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Staff */}
      {data.primaryStaff && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" />Primary Support Worker</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-lg">{data.primaryStaff.staffName}</p>
                <p className="text-sm text-muted-foreground">{data.primaryStaff.shiftCount} shifts ({data.primaryStaff.percentage}%)</p>
              </div>
              <RefreshCw className={`w-8 h-8 ${data.continuityLevel === 'GOOD' ? 'text-green-500' : data.continuityLevel === 'MODERATE' ? 'text-amber-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Breakdown */}
      {data.staffBreakdown?.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Recent Support Workers</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead className="text-right">Shifts</TableHead><TableHead className="text-right">Coverage</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.staffBreakdown.map((s, i) => (
                  <TableRow key={s.staffId}>
                    <TableCell className="font-medium">{s.staffName}{i === 0 && <Badge className="ml-2 bg-green-500 text-white text-[10px]">Primary</Badge>}</TableCell>
                    <TableCell className="text-right">{s.shiftCount}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full" style={{width: `${Math.min(s.percentage, 100)}%`}} /></div>
                        <span className="text-xs w-10 text-right">{s.percentage}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const RELATIONSHIPS = ['FAMILY', 'GUARDIAN', 'NOMINEE', 'SUPPORT_COORDINATOR', 'CASE_MANAGER', 'PLAN_MANAGER', 'LAC', 'DOCTOR', 'SPECIALIST', 'THERAPIST', 'OTHER'];
const CHANNELS = ['PORTAL', 'SMS', 'EMAIL', 'PHONE'];
const POA_TYPES = ['FINANCIAL', 'MEDICAL', 'GENERAL', 'ENDURING'];

function ContactsTab({ participantId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/engagement/contacts/by-participant/${participantId}`)
      .then(r => setContacts(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/engagement/contacts/${editing.id}`, form);
        toast.success('Contact updated');
      } else {
        await api.post('/engagement/contacts', { ...form, participantId });
        toast.success('Contact created');
      }
      setShowForm(false); setEditing(null); load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleDeactivate = async (id) => {
    try { await api.post(`/engagement/contacts/${id}/deactivate`); toast.success('Deactivated'); load(); }
    catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  return (
    <div className="space-y-4" data-testid="contacts-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold">Participant Contacts</h3>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-contact-btn"><Plus className="w-3 h-3 mr-1" />Add Contact</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : contacts.length === 0 ? <p className="text-muted-foreground text-center py-6">No contacts</p> : (
        <div className="space-y-2">
          {contacts.map(c => (
            <Card key={c.id} className={c.status === 'INACTIVE' ? 'opacity-50' : ''}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{c.firstName} {c.lastName}</p>
                      <Badge variant="outline">{c.relationship?.replace(/_/g, ' ')}</Badge>
                      {c.relationshipDetail && <span className="text-xs text-muted-foreground">({c.relationshipDetail})</span>}
                      <Badge variant="secondary">{c.preferredChannel}</Badge>
                      {c.status === 'INACTIVE' && <Badge variant="destructive">Inactive</Badge>}
                      {c.isEmergencyContact && <Badge variant="destructive" className="text-[10px]">Emergency</Badge>}
                      {c.isPrimaryContact && <Badge className="bg-blue-500 text-white text-[10px]">Primary</Badge>}
                      {c.isDecisionMaker && <Badge className="bg-purple-500 text-white text-[10px]">Decision Maker</Badge>}
                      {c.hasPowerOfAttorney && <Badge className="bg-amber-500 text-white text-[10px]">POA ({c.poaType})</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 space-x-3">
                      {c.email && <span>{c.email}</span>}
                      {c.phone && <span>{c.phone}</span>}
                      {c.phoneWork && <span>Work: {c.phoneWork}</span>}
                      {c.organization && <span className="italic">{c.organization}</span>}
                    </div>
                    {(c.addressLine1 || c.suburb) && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {[c.addressLine1, c.addressLine2, c.suburb, c.state, c.postcode].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {c.canReceiveGeneralUpdates && <Badge variant="outline" className="text-[10px]">General</Badge>}
                      {c.canReceiveScheduleChanges && <Badge variant="outline" className="text-[10px]">Schedule</Badge>}
                      {c.canReceiveMedicalUpdates && <Badge variant="outline" className="text-[10px]">Medical</Badge>}
                      {c.canReceiveIncidentNotifications && <Badge variant="outline" className="text-[10px]">Incidents</Badge>}
                      {c.canReceiveBillingUpdates && <Badge variant="outline" className="text-[10px]">Billing</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(c); setShowForm(true); }} data-testid={`edit-contact-${c.id}`}>Edit</Button>
                    {c.status === 'ACTIVE' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeactivate(c.id)} data-testid={`deactivate-contact-${c.id}`}>Deactivate</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Contact' : 'Add Contact'}</DialogTitle></DialogHeader>
          <ContactForm contact={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactForm({ contact, onSave, onCancel }) {
  const [form, setForm] = useState(contact ? {
    firstName: contact.firstName, lastName: contact.lastName, relationship: contact.relationship,
    relationshipDetail: contact.relationshipDetail || '',
    email: contact.email || '', phone: contact.phone || '', phoneWork: contact.phoneWork || '',
    // FRD Section 6.4 - Address fields
    addressLine1: contact.addressLine1 || '', addressLine2: contact.addressLine2 || '',
    suburb: contact.suburb || '', state: contact.state || '',
    postcode: contact.postcode || '', country: contact.country || 'Australia',
    address: contact.address || '', // Legacy field
    organization: contact.organization || '',
    preferredChannel: contact.preferredChannel,
    canReceiveGeneralUpdates: contact.canReceiveGeneralUpdates, canReceiveScheduleChanges: contact.canReceiveScheduleChanges,
    canReceiveMedicalUpdates: contact.canReceiveMedicalUpdates, canReceiveIncidentNotifications: contact.canReceiveIncidentNotifications,
    canReceiveBillingUpdates: contact.canReceiveBillingUpdates,
    isEmergencyContact: contact.isEmergencyContact || false,
    isPrimaryContact: contact.isPrimaryContact || false,
    isDecisionMaker: contact.isDecisionMaker || false,
    hasPowerOfAttorney: contact.hasPowerOfAttorney || false,
    poaType: contact.poaType || '',
    poaDocumentId: contact.poaDocumentId || '',
    portalUserId: contact.portalUserId || '', notes: contact.notes || '',
  } : {
    firstName: '', lastName: '', relationship: 'FAMILY', relationshipDetail: '',
    email: '', phone: '', phoneWork: '',
    addressLine1: '', addressLine2: '', suburb: '', state: '', postcode: '', country: 'Australia',
    address: '', organization: '',
    preferredChannel: 'EMAIL',
    canReceiveGeneralUpdates: true, canReceiveScheduleChanges: true, canReceiveMedicalUpdates: false,
    canReceiveIncidentNotifications: false, canReceiveBillingUpdates: false,
    isEmergencyContact: false, isPrimaryContact: false, isDecisionMaker: false,
    hasPowerOfAttorney: false, poaType: '', poaDocumentId: '',
    portalUserId: '', notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>First Name</Label><Input data-testid="contact-first-name" value={form.firstName} onChange={e => set('firstName', e.target.value)} /></div>
        <div><Label>Last Name</Label><Input data-testid="contact-last-name" value={form.lastName} onChange={e => set('lastName', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Relationship</Label>
          <Select value={form.relationship} onValueChange={v => set('relationship', v)}>
            <SelectTrigger data-testid="contact-relationship"><SelectValue /></SelectTrigger>
            <SelectContent>{RELATIONSHIPS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Relationship Detail</Label><Input value={form.relationshipDetail} onChange={e => set('relationshipDetail', e.target.value)} placeholder="e.g., Mother, GP" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Email</Label><Input data-testid="contact-email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
        <div><Label>Phone</Label><Input data-testid="contact-phone" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Work Phone</Label><Input value={form.phoneWork} onChange={e => set('phoneWork', e.target.value)} /></div>
        <div><Label>Organization</Label><Input value={form.organization} onChange={e => set('organization', e.target.value)} placeholder="For professional contacts" /></div>
      </div>
      
      {/* FRD Section 6.4 - Full Address Fields */}
      <div className="p-3 border rounded-md space-y-3 bg-muted/20">
        <Label className="font-semibold">Address Details</Label>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Address Line 1</Label><Input value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} placeholder="Street address" /></div>
          <div><Label className="text-xs">Address Line 2</Label><Input value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} placeholder="Unit, apt, etc." /></div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div><Label className="text-xs">Suburb</Label><Input value={form.suburb} onChange={e => set('suburb', e.target.value)} /></div>
          <div><Label className="text-xs">State</Label>
            <Select value={form.state} onValueChange={v => set('state', v)}>
              <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                {['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'].map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Postcode</Label><Input value={form.postcode} onChange={e => set('postcode', e.target.value)} maxLength={4} /></div>
          <div><Label className="text-xs">Country</Label><Input value={form.country} onChange={e => set('country', e.target.value)} /></div>
        </div>
      </div>
      
      <div><Label>Preferred Channel</Label>
        <Select value={form.preferredChannel} onValueChange={v => set('preferredChannel', v)}>
          <SelectTrigger data-testid="contact-channel"><SelectValue /></SelectTrigger>
          <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Key Contact Flags */}
      <div className="p-3 border rounded-md space-y-2 bg-muted/30">
        <Label className="font-semibold">Contact Flags</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['isEmergencyContact', 'Emergency Contact'],
            ['isPrimaryContact', 'Primary Contact'],
            ['isDecisionMaker', 'Decision Maker'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="rounded" />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Power of Attorney */}
      <div className="p-3 border rounded-md space-y-2 bg-muted/30">
        <label className="flex items-center gap-2 text-sm cursor-pointer font-semibold">
          <input type="checkbox" checked={form.hasPowerOfAttorney} onChange={e => set('hasPowerOfAttorney', e.target.checked)} className="rounded" />
          Has Power of Attorney
        </label>
        {form.hasPowerOfAttorney && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label className="text-xs">POA Type</Label>
              <Select value={form.poaType} onValueChange={v => set('poaType', v)}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{POA_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">POA Document ID</Label><Input value={form.poaDocumentId} onChange={e => set('poaDocumentId', e.target.value)} placeholder="Link to document" /></div>
          </div>
        )}
      </div>

      {/* Communication Preferences */}
      <div>
        <Label className="mb-2 block">Communication Preferences</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            ['canReceiveGeneralUpdates', 'General Updates'],
            ['canReceiveScheduleChanges', 'Schedule Changes'],
            ['canReceiveMedicalUpdates', 'Medical Updates'],
            ['canReceiveIncidentNotifications', 'Incident Notifications'],
            ['canReceiveBillingUpdates', 'Billing Updates'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} className="rounded" />
              {label}
            </label>
          ))}
        </div>
      </div>
      <div><Label>Portal User ID (optional)</Label><Input data-testid="contact-portal-user" value={form.portalUserId} onChange={e => set('portalUserId', e.target.value)} placeholder="Link to existing portal user" /></div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes..." /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="save-contact-btn" onClick={() => onSave(form)} disabled={!form.firstName || !form.lastName}>Save</Button>
      </div>
    </div>
  );
}

const APT_TYPES = ['GP', 'ALLIED_HEALTH', 'THERAPY', 'FAMILY_MEETING', 'NDIS_REVIEW', 'CASE_CONFERENCE', 'TRANSPORT', 'OTHER'];
const APT_STATUS_COLORS = { SCHEDULED: 'bg-blue-500 text-white', CONFIRMED: 'bg-green-500 text-white', COMPLETED: 'bg-slate-500 text-white', CANCELLED: 'bg-red-500 text-white' };

function GroupSessionsTab({ participantId }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/group-sessions/participant/${participantId}/sessions`);
        setSessions(res.data.data || []);
      } catch { setSessions([]); }
      finally { setLoading(false); }
    };
    load();
  }, [participantId]);

  const STATUS_COLORS = { draft: 'bg-slate-100 text-slate-700', published: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700', completed: 'bg-emerald-100 text-emerald-700' };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="group-sessions-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Linked Group Sessions ({sessions.length})</h3>
      </div>
      {sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No group sessions linked to this participant.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <Card key={s.id} className="hover:bg-muted/30 transition-colors">
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-indigo-500 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{s.title}</p>
                      <p className="text-xs text-muted-foreground">{s.session_code} &middot; {s.session_type?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span>{s.start_datetime ? new Date(s.start_datetime).toLocaleDateString() : ''}</span>
                    <span className="text-muted-foreground">{s.start_datetime ? new Date(s.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : ''}</span>
                    <Badge className={STATUS_COLORS[s.status] || 'bg-slate-100'}>{s.status}</Badge>
                    <span className="font-mono">{s.staff_count || 0}S / {s.participant_count || 0}P</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AppointmentsTab({ participantId }) {
  const [apts, setApts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/appointments?participantId=${participantId}&pageSize=50`)
      .then(r => setApts(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action) => {
    try {
      if (action === 'cancel') {
        await api.post(`/appointments/${id}/cancel`, { reason: 'Cancelled from UI' });
      } else {
        await api.post(`/appointments/${id}/${action}`);
      }
      toast.success(`Appointment ${action}ed`);
      load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleSave = async (form) => {
    try {
      if (editing) {
        await api.put(`/appointments/${editing.id}`, form);
      } else {
        await api.post('/appointments', { ...form, participantId });
      }
      toast.success(editing ? 'Updated' : 'Created');
      setShowForm(false); setEditing(null); load();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get(`/appointments/export-csv?participantId=${participantId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'appointments.csv'; a.click(); window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { notify.error(e, 'EXPORT_FAILED'); }
  };

  return (
    <div className="space-y-4" data-testid="appointments-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold">Appointments</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-appointments-btn">Export CSV</Button>
          <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-appointment-btn"><Plus className="w-3 h-3 mr-1" />New Appointment</Button>
        </div>
      </div>
      {loading ? <p className="text-muted-foreground">Loading...</p> : apts.length === 0 ? <p className="text-muted-foreground text-center py-6">No appointments</p> : (
        <div className="space-y-2">
          {apts.map(a => (
            <Card key={a.id}>
              <CardContent className="py-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={APT_STATUS_COLORS[a.status] || ''}>{a.status}</Badge>
                      <span className="font-mono text-xs">{a.appointmentNo}</span>
                      <Badge variant="outline">{a.appointmentType}</Badge>
                    </div>
                    <p className="font-medium mt-1">{a.title}</p>
                    <div className="text-xs text-muted-foreground mt-1 space-x-3">
                      <span>{a.scheduledStart?.replace('T', ' ')}</span>
                      {a.location && <span>{a.location}</span>}
                      {a.providerName && <span>{a.providerName}</span>}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {a.transportRequired && <Badge variant="outline" className="text-[10px]">Transport</Badge>}
                      {a.staffRequired && <Badge variant="outline" className="text-[10px]">Staff</Badge>}
                      {a.assignedStaffName && <Badge variant="secondary" className="text-[10px]">{a.assignedStaffName}</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {a.status === 'SCHEDULED' && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'confirm')}>Confirm</Button>}
                    {(a.status === 'SCHEDULED' || a.status === 'CONFIRMED') && <Button size="sm" variant="outline" onClick={() => handleAction(a.id, 'complete')}>Complete</Button>}
                    {a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleAction(a.id, 'cancel')}>Cancel</Button>}
                    {a.status !== 'COMPLETED' && a.status !== 'CANCELLED' && <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setShowForm(true); }}>Edit</Button>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Appointment' : 'New Appointment'}</DialogTitle></DialogHeader>
          <AppointmentForm appointment={editing} onSave={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppointmentForm({ appointment, onSave, onCancel }) {
  const [form, setForm] = useState(appointment ? {
    title: appointment.title, appointmentType: appointment.appointmentType,
    scheduledStart: appointment.scheduledStart || '', scheduledEnd: appointment.scheduledEnd || '',
    location: appointment.location || '', providerName: appointment.providerName || '',
    contactDetails: appointment.contactDetails || '', transportRequired: appointment.transportRequired || false,
    staffRequired: appointment.staffRequired || false, assignedStaffId: appointment.assignedStaffId || '', notes: appointment.notes || '',
  } : {
    title: '', appointmentType: 'GP', scheduledStart: '', scheduledEnd: '',
    location: '', providerName: '', contactDetails: '', transportRequired: false,
    staffRequired: false, assignedStaffId: '', notes: '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-3">
      <div><Label>Title</Label><Input data-testid="apt-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select value={form.appointmentType} onValueChange={v => set('appointmentType', v)}>
            <SelectTrigger data-testid="apt-type"><SelectValue /></SelectTrigger>
            <SelectContent>{APT_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Provider</Label><Input value={form.providerName} onChange={e => set('providerName', e.target.value)} placeholder="Dr. Smith" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Start</Label><Input type="datetime-local" data-testid="apt-start" value={form.scheduledStart} onChange={e => set('scheduledStart', e.target.value)} /></div>
        <div><Label>End</Label><Input type="datetime-local" data-testid="apt-end" value={form.scheduledEnd} onChange={e => set('scheduledEnd', e.target.value)} /></div>
      </div>
      <div><Label>Location</Label><Input value={form.location} onChange={e => set('location', e.target.value)} /></div>
      <div className="flex gap-4">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.transportRequired} onChange={e => set('transportRequired', e.target.checked)} className="rounded" />Transport Required</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.staffRequired} onChange={e => set('staffRequired', e.target.checked)} className="rounded" />Staff Required</label>
      </div>
      <div><Label>Notes</Label><Input value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="save-apt-btn" onClick={() => onSave(form)} disabled={!form.title || !form.scheduledStart}>Save</Button>
      </div>
    </div>
  );
}

function NotesTab({ participantId }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    api.get(`/notes/by-participant/${participantId}?pageSize=50`).then(r => setNotes(r.data.items || r.data.data || [])).catch(() => {});
  }, [participantId]);
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Content</TableHead><TableHead>Author</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {notes.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No notes</TableCell></TableRow>
          : notes.map(n => (
            <TableRow key={n.id} className="hover:bg-muted/60">
              <TableCell className="font-mono text-xs">{n.created_at?.slice(0,10)}</TableCell>
              <TableCell><StatusBadge status={n.note_type || n.template_key || '-'} /></TableCell>
              <TableCell className="font-medium max-w-xs truncate">{n.content || n.title || '-'}</TableCell>
              <TableCell className="text-sm">{n.author_name || 'N/A'}</TableCell>
              <TableCell><StatusBadge status={n.status} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
}

function FormsTab({ participantId }) {
  const [assignments, setAssignments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ template_id: '', due_date: '', priority: 'NORMAL' });
  const navigate = useNavigate();

  const loadAssignments = useCallback(() => {
    api.get(`/forms/assignments?target_type=PARTICIPANT&target_id=${participantId}&pageSize=50`)
      .then(r => setAssignments(r.data.items || []))
      .catch(() => {});
  }, [participantId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  useEffect(() => {
    if (showAssign) {
      api.get('/forms/templates?status=PUBLISHED&pageSize=100').then(r => setTemplates(r.value?.data?.items || r.data?.items || [])).catch(() => {});
    }
  }, [showAssign]);

  const handleAssign = async () => {
    if (!assignForm.template_id) { notify.warn('VALIDATION_REQUIRED'); return; }
    try {
      await api.post('/forms/assignments', {
        template_id: assignForm.template_id,
        target_type: 'PARTICIPANT',
        target_id: participantId,
        due_date: assignForm.due_date || null,
        priority: assignForm.priority,
      });
      toast.success('Form assigned');
      setShowAssign(false);
      setAssignForm({ template_id: '', due_date: '', priority: 'NORMAL' });
      loadAssignments();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const STATUS_COLORS = { PENDING: 'secondary', IN_PROGRESS: 'default', SUBMITTED: 'default', APPROVED: 'default', REJECTED: 'destructive' };

  return (
    <div className="space-y-4" data-testid="forms-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Assigned Forms</h3>
        <Button size="sm" onClick={() => setShowAssign(true)} data-testid="assign-form-to-participant-btn">
          <Plus className="w-4 h-4 mr-1" />Assign Form
        </Button>
      </div>

      {assignments.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No forms assigned to this participant yet</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card key={a.id} className="hover:shadow-sm transition-shadow" data-testid={`participant-form-${a.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => navigate(`/forms/assignments/${a.id}/fill`)}>
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{a.template_name || 'Form'}</p>
                    <p className="text-xs text-muted-foreground">
                      Priority: {a.priority}{a.due_date ? ` · Due: ${a.due_date}` : ''}{a.assigned_at ? ` · Assigned: ${a.assigned_at.slice(0,10)}` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); navigate(`/forms/assignments/${a.id}/print`); }} data-testid={`print-form-${a.id}`}>
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Badge variant={STATUS_COLORS[a.status] || 'secondary'}>{a.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign Form</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Form Template *</Label>
              <Select value={assignForm.template_id} onValueChange={v => setAssignForm(p => ({ ...p, template_id: v }))}>
                <SelectTrigger data-testid="participant-assign-template"><SelectValue placeholder="Select a form..." /></SelectTrigger>
                <SelectContent>{templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={assignForm.due_date} onChange={e => setAssignForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={assignForm.priority} onValueChange={v => setAssignForm(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} data-testid="confirm-participant-assign">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TeamsTab({ participantId }) {
  const [teams, setTeams] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/teams/by-participant/${participantId}`).then(r => setTeams(r.data.items || [])).catch(() => {});
  }, [participantId]);
  return (
    <div className="space-y-4" data-testid="teams-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Linked Teams</h3>
      </div>
      {teams.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">No teams linked to this participant</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {teams.map(t => (
            <Card key={t.id} className="hover:shadow-sm cursor-pointer" onClick={() => navigate(`/teams/${t.id}`)} data-testid={`participant-team-${t.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t.team_name}</p>
                    <p className="text-xs text-muted-foreground">{t.member_count || 0} members{t.leader_name ? ` · Leader: ${t.leader_name}` : ''}</p>
                  </div>
                </div>
                <Badge>{t.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


function TimelineTab({ participantId }) {
  const [timeline, setTimeline] = useState([]);
  useEffect(() => {
    api.get(`/participants/${participantId}/timeline?limit=50`).then(r => setTimeline(r.data.data || [])).catch(() => {});
  }, [participantId]);
  return (
    <div className="space-y-2">
      {timeline.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No activity</CardContent></Card>
      : timeline.map((e, i) => {
        const Icon = TIMELINE_ICONS[e.type] || Clock;
        return (
          <div key={i} className="flex gap-3 py-2 border-b last:border-0">
            <div className="mt-1 h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
              <Icon className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{e.title}</p>
                <StatusBadge status={e.action?.toUpperCase()} className="text-[9px]" />
              </div>
              <p className="text-xs text-muted-foreground">{e.description}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{e.timestamp?.replace('T',' ').slice(0,19)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function CarePlansTab({ participantId }) {
  const [plans, setPlans] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', medical_alerts: '', personal_care_needs: '', dietary_requirements: '', routine_morning: '', important_notes: '', review_due_date: '' });
  useEffect(() => { api.get(`/careplans/by-participant/${participantId}?pageSize=50`).then(r => setPlans(r.data.items || r.data.data || [])).catch(() => {}); }, [participantId]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/careplans', { participant_id: participantId, ...form });
      const r = await api.get(`/careplans/by-participant/${participantId}?pageSize=50`);
      setPlans(r.data.items || r.data.data || []); setCreateOpen(false);
    } catch (e) { /* toast would go here */ }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/careplans/${id}/${action}`);
      const r = await api.get(`/careplans/by-participant/${participantId}?pageSize=50`);
      setPlans(r.data.items || r.data.data || []);
    } catch (e) { /* toast */ }
  };

  const exportPdf = async (id) => {
    try {
      const r = await api.get(`/careplans/${id}/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'careplan.pdf'; a.click();
    } catch (e) {}
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Care Plans</h3><Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-3 w-3 mr-1" /> New Plan</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Review Due</Label><Input type="date" value={form.review_due_date} onChange={(e) => setForm({...form, review_due_date: e.target.value})} className="h-8 text-sm" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Medical Alerts</Label><Input value={form.medical_alerts} onChange={(e) => setForm({...form, medical_alerts: e.target.value})} className="h-8 text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs">Personal Care Needs</Label><Input value={form.personal_care_needs} onChange={(e) => setForm({...form, personal_care_needs: e.target.value})} className="h-8 text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs">Important Notes</Label><Input value={form.important_notes} onChange={(e) => setForm({...form, important_notes: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-2"><Button size="sm" type="submit">Create</Button><Button size="sm" variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      <Table><TableHeader><TableRow><TableHead>Plan No</TableHead><TableHead>Title</TableHead><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{plans.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-4 text-muted-foreground">No care plans</TableCell></TableRow>
          : plans.map(p => (<TableRow key={p.id}><TableCell className="font-mono text-xs">{p.plan_no}</TableCell><TableCell className="font-medium">{p.title}</TableCell><TableCell>{p.version}</TableCell><TableCell><StatusBadge status={p.status} /></TableCell>
            <TableCell><div className="flex gap-1">
              {p.status === 'DRAFT' && <Button size="sm" variant="ghost" onClick={() => doAction(p.id, 'submit-review')}>Submit</Button>}
              {p.status === 'REVIEW' && <Button size="sm" variant="ghost" onClick={() => doAction(p.id, 'approve')}>Approve</Button>}
              <Button size="sm" variant="ghost" onClick={() => exportPdf(p.id)}>PDF</Button>
            </div></TableCell></TableRow>))}</TableBody></Table>
    </div>
  );
}

function RisksTab({ participantId }) {
  const [risks, setRisks] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: '', risk_type: 'FALL', likelihood: 'MEDIUM', impact: 'MEDIUM', risk_description: '', control_measures: '' });
  useEffect(() => { api.get(`/risk/by-participant/${participantId}?pageSize=50`).then(r => setRisks(r.data.items || r.data.data || [])).catch(() => {}); }, [participantId]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/risk', { participant_id: participantId, ...form });
      const r = await api.get(`/risk/by-participant/${participantId}?pageSize=50`);
      setRisks(r.data.items || r.data.data || []); setCreateOpen(false);
    } catch (e) {}
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/risk/${id}/${action}`);
      const r = await api.get(`/risk/by-participant/${participantId}?pageSize=50`);
      setRisks(r.data.items || r.data.data || []);
    } catch (e) {}
  };

  const severityColor = (score) => score >= 6 ? 'text-red-600' : score >= 3 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Risk Assessments</h3><Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-3 w-3 mr-1" /> New Risk</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.risk_type} onValueChange={(v) => setForm({...form, risk_type: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['FALL','MEDICATION','BEHAVIOUR','TRANSPORT','ENVIRONMENT','MISSING_PERSON','OTHER'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Likelihood</Label><Select value={form.likelihood} onValueChange={(v) => setForm({...form, likelihood: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['LOW','MEDIUM','HIGH'].map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label className="text-xs">Impact</Label><Select value={form.impact} onValueChange={(v) => setForm({...form, impact: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['LOW','MEDIUM','HIGH'].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={form.risk_description} onChange={(e) => setForm({...form, risk_description: e.target.value})} required /></div>
          <div className="space-y-1"><Label className="text-xs">Control Measures</Label><Input value={form.control_measures} onChange={(e) => setForm({...form, control_measures: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-2"><Button size="sm" type="submit">Create</Button><Button size="sm" variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      <Table><TableHeader><TableRow><TableHead>No</TableHead><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Score</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{risks.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-4 text-muted-foreground">No risks</TableCell></TableRow>
          : risks.map(r => (<TableRow key={r.id}><TableCell className="font-mono text-xs">{r.risk_no}</TableCell><TableCell className="font-medium">{r.title}</TableCell><TableCell><StatusBadge status={r.risk_type} /></TableCell>
            <TableCell><span className={`font-bold ${severityColor(r.risk_score)}`}>{r.risk_score}</span> <span className="text-xs text-muted-foreground">({r.likelihood}/{r.impact})</span></TableCell>
            <TableCell><StatusBadge status={r.status} /></TableCell>
            <TableCell><div className="flex gap-1">
              {r.status === 'ACTIVE' && !r.approved_by_user_id && <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'approve')}>Approve</Button>}
              {r.status !== 'CLOSED' && <Button size="sm" variant="ghost" onClick={() => doAction(r.id, 'close')}>Close</Button>}
            </div></TableCell></TableRow>))}</TableBody></Table>
    </div>
  );
}

function GoalsTab({ participantId }) {
  const [goals, setGoals] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [progressGoal, setProgressGoal] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'INDEPENDENCE', target_date: '' });
  const [progressForm, setProgressForm] = useState({ note: '', progress_percent: 0 });
  useEffect(() => { api.get(`/goals/by-participant/${participantId}?pageSize=50`).then(r => setGoals(r.data.items || r.data.data || [])).catch(() => {}); }, [participantId]);

  const refresh = async () => { const r = await api.get(`/goals/by-participant/${participantId}?pageSize=50`); setGoals(r.data.items || r.data.data || []); };
  const create = async (e) => { e.preventDefault(); try { await api.post('/goals', { participant_id: participantId, ...form }); refresh(); setCreateOpen(false); } catch (e) {} };
  const addProgress = async () => { if (!progressGoal) return; try { await api.post(`/goals/${progressGoal.id}/progress`, progressForm); refresh(); setProgressGoal(null); } catch (e) {} };
  const archive = async (id) => { try { await api.post(`/goals/${id}/archive`); refresh(); } catch (e) {} };

  const lastPercent = (g) => { const ups = g.progress_updates || []; return ups.length > 0 ? ups[ups.length-1].progress_percent || 0 : 0; };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Goals & Outcomes</h3><Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-3 w-3 mr-1" /> New Goal</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={create} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Category</Label><Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['INDEPENDENCE','COMMUNITY_ACCESS','HEALTH','SKILLS','BEHAVIOUR','OTHER'].map(c => <SelectItem key={c} value={c}>{c.replace(/_/g,' ')}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Target Date</Label><Input type="date" value={form.target_date} onChange={(e) => setForm({...form, target_date: e.target.value})} className="h-8 text-sm" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Description</Label><Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-2"><Button size="sm" type="submit">Create</Button><Button size="sm" variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      {goals.length === 0 ? <Card><CardContent className="py-8 text-center text-muted-foreground">No goals</CardContent></Card>
      : goals.map(g => (
        <Card key={g.id} className="hover:border-primary/20 transition-colors">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-2">
              <div><p className="font-medium">{g.title}</p><p className="text-xs text-muted-foreground">{g.goal_no} | <StatusBadge status={g.category} /> | Target: {g.target_date || '-'}</p></div>
              <div className="flex items-center gap-2"><StatusBadge status={g.status} />
                {g.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => { setProgressGoal(g); setProgressForm({ note: '', progress_percent: lastPercent(g) }); }}>+ Progress</Button>}
                {g.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" onClick={() => archive(g.id)}>Archive</Button>}
              </div>
            </div>
            {g.description && <p className="text-sm text-muted-foreground mb-2">{g.description}</p>}
            <div className="w-full bg-muted rounded-full h-2.5 mb-1"><div className="bg-primary h-2.5 rounded-full transition-all" style={{width: `${lastPercent(g)}%`}} /></div>
            <p className="text-xs text-muted-foreground">{lastPercent(g)}% complete | {(g.progress_updates || []).length} update(s)</p>
          </CardContent>
        </Card>
      ))}
      {progressGoal && (
        <Dialog open={!!progressGoal} onOpenChange={() => setProgressGoal(null)}>
          <DialogContent><DialogHeader><DialogTitle>Update Progress: {progressGoal.title}</DialogTitle><DialogDescription>Add a progress note</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2"><Label>Progress (%)</Label><Input type="number" min="0" max="100" value={progressForm.progress_percent} onChange={(e) => setProgressForm({...progressForm, progress_percent: parseInt(e.target.value) || 0})} /></div>
              <div className="space-y-2"><Label>Note</Label><textarea className="w-full min-h-[60px] rounded-lg border bg-background px-3 py-2 text-sm" value={progressForm.note} onChange={(e) => setProgressForm({...progressForm, note: e.target.value})} required /></div>
              <Button className="w-full" onClick={addProgress} disabled={!progressForm.note}>Save Progress</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function MedicationsTab({ participantId }) {
  const [meds, setMeds] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(null);
  const [form, setForm] = useState({ name:'',generic_name:'',form:'TABLET',route:'ORAL',dosage:'',strength:'',prescribed_by:'',instructions:'',prn:false,requires_double_sign:false });
  const [schedForm, setSchedForm] = useState({ schedule_type:'DAILY',times:['08:00'],days_of_week:[],instructions:'' });

  const refresh = async () => {
    const [mR, sR] = await Promise.all([
      api.get(`/medications/by-participant/${participantId}?pageSize=100`),
      api.get(`/medschedule/by-participant/${participantId}?pageSize=100`),
    ]);
    setMeds(mR.data.items || mR.data.data || []);
    setSchedules(sR.data.items || sR.data.data || []);
  };
  useEffect(() => { refresh(); }, [participantId]); // eslint-disable-line

  const createMed = async (e) => {
    e.preventDefault();
    try { await api.post('/medications', { participant_id: participantId, ...form }); refresh(); setCreateOpen(false); } catch(e) {}
  };
  const stopMed = async (id) => { try { await api.post(`/medications/${id}/stop`); refresh(); } catch(e) {} };
  const archiveMed = async (id) => { try { await api.post(`/medications/${id}/archive`); refresh(); } catch(e) {} };
  const addSchedule = async () => {
    if (!schedOpen) return;
    try {
      await api.post('/medschedule', { participant_id: participantId, medication_id: schedOpen, ...schedForm });
      refresh(); setSchedOpen(null);
    } catch(e) {}
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between"><h3 className="font-bold">Medications</h3><Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-3 w-3 mr-1" /> Add Medication</Button></div>
      {createOpen && (
        <Card><CardContent className="p-4"><form onSubmit={createMed} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Form</Label><Select value={form.form} onValueChange={(v) => setForm({...form, form: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['TABLET','LIQUID','INJECTION','CAPSULE','PATCH','OTHER'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label className="text-xs">Route</Label><Select value={form.route} onValueChange={(v) => setForm({...form, route: v})}><SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger><SelectContent>{['ORAL','TOPICAL','INHALATION','SUBCUTANEOUS','OTHER'].map(r_ => <SelectItem key={r_} value={r_}>{r_}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Dosage</Label><Input value={form.dosage} onChange={(e) => setForm({...form, dosage: e.target.value})} required className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Strength</Label><Input value={form.strength} onChange={(e) => setForm({...form, strength: e.target.value})} className="h-8 text-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Prescribed By</Label><Input value={form.prescribed_by} onChange={(e) => setForm({...form, prescribed_by: e.target.value})} className="h-8 text-sm" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Instructions</Label><Input value={form.instructions} onChange={(e) => setForm({...form, instructions: e.target.value})} className="h-8 text-sm" /></div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.prn} onChange={(e) => setForm({...form, prn: e.target.checked})} /> PRN (as needed)</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.requires_double_sign} onChange={(e) => setForm({...form, requires_double_sign: e.target.checked})} /> Requires double sign</label>
          </div>
          <div className="flex gap-2"><Button size="sm" type="submit">Create</Button><Button size="sm" variant="ghost" type="button" onClick={() => setCreateOpen(false)}>Cancel</Button></div>
        </form></CardContent></Card>
      )}
      <Table><TableHeader><TableRow><TableHead>No</TableHead><TableHead>Name</TableHead><TableHead>Dosage</TableHead><TableHead>Form</TableHead><TableHead>PRN</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
        <TableBody>{meds.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-4 text-muted-foreground">No medications</TableCell></TableRow>
          : meds.map(m => (<TableRow key={m.id}><TableCell className="font-mono text-xs">{m.medication_no}</TableCell><TableCell className="font-medium">{m.name}</TableCell>
            <TableCell className="text-sm">{m.dosage}</TableCell><TableCell>{m.form}</TableCell><TableCell>{m.prn ? 'Yes' : '-'}</TableCell><TableCell><StatusBadge status={m.status} /></TableCell>
            <TableCell><div className="flex gap-1">
              {m.status === 'ACTIVE' && <Button size="sm" variant="ghost" onClick={() => setSchedOpen(m.id)}>Schedule</Button>}
              {m.status === 'ACTIVE' && <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => stopMed(m.id)}>Stop</Button>}
              {m.status !== 'ARCHIVED' && <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => archiveMed(m.id)}>Archive</Button>}
            </div></TableCell></TableRow>))}</TableBody></Table>

      {/* Schedule Dialog */}
      {schedOpen && (
        <Dialog open={!!schedOpen} onOpenChange={() => setSchedOpen(null)}>
          <DialogContent><DialogHeader><DialogTitle>Add Schedule</DialogTitle><DialogDescription>Set medication times</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={schedForm.schedule_type} onValueChange={(v) => setSchedForm({...schedForm, schedule_type: v})}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger><SelectContent>{['DAILY','WEEKLY','SPECIFIC_DAYS','PRN'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              {schedForm.schedule_type !== 'PRN' && <div className="space-y-1"><Label className="text-xs">Times (comma-separated HH:MM)</Label><Input value={schedForm.times.join(',')} onChange={(e) => setSchedForm({...schedForm, times: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} className="h-8 text-sm font-mono" placeholder="08:00,12:00,20:00" /></div>}
              <div className="space-y-1"><Label className="text-xs">Instructions</Label><Input value={schedForm.instructions} onChange={(e) => setSchedForm({...schedForm, instructions: e.target.value})} className="h-8 text-sm" /></div>
              <Button className="w-full" size="sm" onClick={addSchedule}>Add Schedule</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Schedules */}
      {schedules.length > 0 && (<>
        <h4 className="font-medium text-sm mt-4">Schedules</h4>
        <div className="space-y-2">{schedules.map(s => (
          <div key={s.id} className="border rounded-lg p-3 text-sm flex items-center justify-between">
            <div><span className="font-medium">{s.medication_name || 'Medication'}</span> <StatusBadge status={s.schedule_type} className="ml-2" /><p className="text-xs text-muted-foreground mt-0.5">Times: {(s.times || []).join(', ') || 'PRN'} {s.instructions && `| ${s.instructions}`}</p></div>
          </div>
        ))}</div>
      </>)}
    </div>
  );
}


function ParticipantProfileV3OverviewWrapper({ participant, participantId, accessControl, dashboard, canEditOverview }) {
  const [snapshot, setSnapshot] = useState(null);
  const [overviewProfile, setOverviewProfile] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const fetchAll = useCallback(() => {
    api.get(`/participants/${participantId}/support-snapshot`).then(r => setSnapshot(r.data?.data)).catch(() => {});
    api.get(`/participants/${participantId}/overview-profile`).then(r => setOverviewProfile(r.data?.data || {})).catch(() => {});
  }, [participantId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Lazy import edit drawer
  const { ParticipantOverviewEditDrawer } = require('@/components/participants/profile/overview/ParticipantOverviewEditDrawer');

  return (
    <>
      <ParticipantProfileV3Layout
        participant={participant}
        participantId={participantId}
        accessControl={accessControl}
        dashboard={dashboard}
        snapshot={snapshot}
        overviewProfile={overviewProfile}
        canEditOverview={canEditOverview}
        onEditOverview={() => setEditOpen(true)}
        onEditSnapshot={() => setEditOpen(true)}
        onRefreshSnapshot={fetchAll}
      />
      {editOpen && (
        <ParticipantOverviewEditDrawer
          participantId={participantId}
          currentProfile={overviewProfile || {}}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); fetchAll(); }}
        />
      )}
    </>
  );
}



export default function ParticipantDetailPage() {
  const { dangerConfirm } = useAppDialog();
  const { participantId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';
  const { user } = useAuth();
  const canViewCharts = (user?.permissions || []).includes('participant_charts.view');
  const canViewParticipantDocs = (user?.permissions || []).includes('participants.view_documents');
  const canViewSnapshot = (user?.permissions || []).includes('participants.view_support_snapshot');
  const [participant, setParticipant] = useState(null);
  const [fundingPlans, setFundingPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // RBAC Access Control
  const [accessControl, setAccessControl] = useState({
    is_full_access: true,
    can_edit: true,
    can_view_funding: true,
    can_view_ndis: true,
    can_view_documents: true,
    can_view_incidents: true,
    can_export_pdf: true,
    show_funding_amount: true,
    show_ndis_number: true,
    blocked_tabs: [],
  });

  // Dialogs
  const [fundingDialog, setFundingDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [riskAlerts, setRiskAlerts] = useState(null);
  const [chartsFlagEnabled, setChartsFlagEnabled] = useState(false);
  const [participantDocsFlagEnabled, setParticipantDocsFlagEnabled] = useState(false);
  const [snapshotFlagEnabled, setSnapshotFlagEnabled] = useState(false);
  const [snapshotEditFlagEnabled, setSnapshotEditFlagEnabled] = useState(false);
  const [snapshotIntelFlagEnabled, setSnapshotIntelFlagEnabled] = useState(false);
  const [headerV2Enabled, setHeaderV2Enabled] = useState(false);
  const [overviewV2Enabled, setOverviewV2Enabled] = useState(false);
  const [overviewEditEnabled, setOverviewEditEnabled] = useState(false);
  const [profileV3Enabled, setProfileV3Enabled] = useState(false);
  const [timelineV2Enabled, setTimelineV2Enabled] = useState(false);

  // Fetch feature flags to decide tab visibility (hidden if OFF)
  useEffect(() => {
    let cancelled = false;
    api.get('/production/feature-flags/mine')
      .then(r => {
        if (cancelled) return;
        const flags = r?.data?.data || {};
        setChartsFlagEnabled(!!flags.charts_module_enabled);
        setParticipantDocsFlagEnabled(!!flags.participant_documents_enabled);
        setSnapshotFlagEnabled(!!flags.participant_support_snapshot_enabled);
        setSnapshotEditFlagEnabled(!!flags.participant_support_snapshot_edit_enabled);
        setSnapshotIntelFlagEnabled(!!flags.participant_support_snapshot_intelligence_enabled);
        setHeaderV2Enabled(!!flags.participant_profile_header_v2_enabled);
        setOverviewV2Enabled(!!flags.participant_overview_v2_enabled);
        setOverviewEditEnabled(!!flags.participant_overview_edit_enabled);
        setProfileV3Enabled(!!flags.participant_profile_ui_v3_enabled);
        setTimelineV2Enabled(!!flags.participant_timeline_enabled);
      })
      .catch(() => {
        if (!cancelled) {
          setChartsFlagEnabled(false);
          setParticipantDocsFlagEnabled(false);
          setSnapshotFlagEnabled(false);
          setSnapshotEditFlagEnabled(false);
          setSnapshotIntelFlagEnabled(false);
          setHeaderV2Enabled(false);
          setOverviewV2Enabled(false);
          setOverviewEditEnabled(false);
          setProfileV3Enabled(false);
          setTimelineV2Enabled(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const showChartsTab = chartsFlagEnabled && canViewCharts;
  const showDocumentsTab = participantDocsFlagEnabled && canViewParticipantDocs;
  const showSnapshot = snapshotFlagEnabled && canViewSnapshot;

  // Forms
  const [fpForm, setFpForm] = useState({ plan_name:'', start_date:'', end_date:'', total_budget:'', core:'', capacity:'', capital:'' });
  const [siForm, setSiForm] = useState({ service_catalog_id:'', funding_plan_id:'', day_of_week:'0', start_time:'09:00', end_time:'13:00' });

  const fetchData = useCallback(async () => {
    try {
      // Essential data - participant info (all roles can access)
      const pRes = await api.get(`/participants/${participantId}`);
      setParticipant(pRes.data.data);
      
      // Store access control metadata from API response
      if (pRes.data.access) {
        setAccessControl(pRes.data.access);
      }
      
      // Admin-only data - gracefully handle permission errors
      const [fpRes, txRes, siRes, svcRes, stRes, brRes] = await Promise.allSettled([
        api.get(`/funding/plans/by-participant/${participantId}`),
        api.get(`/funding/transactions/by-participant/${participantId}`),
        api.get('/services/participant-items'),
        api.get('/services/catalog'),
        api.get('/org/states'),
        api.get('/org/branches'),
      ]);
      
      if (fpRes.status === 'fulfilled') setFundingPlans(fpRes.value.data.data || []);
      if (txRes.status === 'fulfilled') setTransactions(txRes.value.data.data || []);
      if (siRes.status === 'fulfilled') setServiceItems((siRes.value.data.data || []).filter(i => i.participant_id === participantId));
      if (svcRes.status === 'fulfilled') setServices(svcRes.value.data.data || []);
      if (stRes.status === 'fulfilled') setStates(stRes.value.data.data || []);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.data || []);

      // Load dashboard + risk alerts (non-blocking)
      const [dashR, riskR] = await Promise.allSettled([
        api.get(`/participants/${participantId}/dashboard`),
        api.get(`/participants/${participantId}/risk-alerts`),
      ]);
      if (dashR.status === 'fulfilled') setDashboard(dashR.value.data.data);
      if (riskR.status === 'fulfilled') setRiskAlerts(riskR.value.data.data);
    } catch (e) { notify.error(e, 'PARTICIPANT_LOAD_FAILED'); }
  }, [participantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createFundingPlan = async (e) => {
    e.preventDefault();
    try {
      const cats = [];
      if (fpForm.core) cats.push({ type:'CORE', allocated: parseFloat(fpForm.core) });
      if (fpForm.capacity) cats.push({ type:'CAPACITY', allocated: parseFloat(fpForm.capacity) });
      if (fpForm.capital) cats.push({ type:'CAPITAL', allocated: parseFloat(fpForm.capital) });
      const total = parseFloat(fpForm.total_budget) || cats.reduce((s,c) => s + c.allocated, 0);
      await api.post('/funding/plans', {
        participant_id: participantId, plan_name: fpForm.plan_name,
        start_date: fpForm.start_date, end_date: fpForm.end_date,
        total_budget: total, categories: cats,
        state_id: participant?.state_id, branch_id: participant?.branch_id,
      });
      toast.success('Funding plan created'); setFundingDialog(false); fetchData();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
  };

  const createServiceItem = async (e) => {
    e.preventDefault();
    try {
      await api.post('/services/participant-items', {
        participant_id: participantId, ...siForm,
        day_of_week: parseInt(siForm.day_of_week),
        state_id: participant?.state_id, branch_id: participant?.branch_id,
      });
      toast.success('Service item created'); setItemDialog(false); fetchData();
    } catch (e) { notify.error(e, 'SERVICE_ITEM_CREATE_FAILED'); }
  };

  const generateShifts = async (id) => {
    setGenerating(id);
    try {
      const res = await api.post(`/services/participant-items/${id}/generate-shifts`);
      toast.success(`Generated ${res.data.data?.length || 0} shifts`);
      fetchData();
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
    finally { setGenerating(null); }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await api.get(`/participants/${participantId}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participant_${participant.participant_code || participantId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      notify.error(null, 'EXPORT_FAILED');
    }
  };

  if (!participant) return <div className="py-12 text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" size="sm" onClick={() => navigate('/participants/list')} className="-ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to participants
      </Button>

      {/* Header */}
      {headerV2Enabled ? (
        <div className="flex items-start justify-between gap-4" data-testid="participant-header-wrapper">
          <div className="flex-1 min-w-0">
            <ParticipantHeaderV2 participant={participant} showNdis={accessControl.show_ndis_number} canEdit={accessControl.can_edit} onPhotoUpdated={fetchData} />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {accessControl.can_export_pdf && (
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export PDF
              </Button>
            )}
            <StatusBadge status={participant.participant_status || participant.status || 'active'} />
            {accessControl.can_edit && (
              <Select value={participant.participant_status || 'ACTIVE'} onValueChange={async v => {
                const isExit = v === 'EXITED';
                const result = await dangerConfirm({
                  title: isExit ? 'Exit participant?' : `Change status to ${v}?`,
                  description: isExit
                    ? 'The participant will be marked as exited. Provide an exit reason for audit.'
                    : 'Provide a reason so the audit trail captures why the status changed.',
                  confirmLabel: isExit ? 'Exit participant' : 'Change status',
                  reasonRequired: true,
                  variant: isExit ? 'danger' : 'warning',
                  testId: 'participant-status-change-confirm',
                });
                if (!result) return;
                try { await api.patch(`/participants/${participantId}/status`, { new_status: v, reason: result.reason || '', exit_reason: isExit ? 'OTHER' : undefined }); toast.success(`Status changed to ${v}`); fetchData(); }
                catch (e) { notify.error(e, 'PARTICIPANT_UPDATE_FAILED'); }
              }}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-change"><SelectValue /></SelectTrigger>
                <SelectContent>{['ENQUIRY','ONBOARDING','ACTIVE','ON_HOLD','INACTIVE','EXITED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {participant.preferred_name ? `${participant.preferred_name} (${participant.first_name} ${participant.last_name})` : `${participant.first_name} ${participant.last_name}`}
            </h1>
            {accessControl.show_ndis_number && participant.ndis_number && (
              <p className="text-muted-foreground font-mono text-sm mt-1">NDIS: {participant.ndis_number}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {accessControl.can_export_pdf && (
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export PDF
              </Button>
            )}
            <StatusBadge status={participant.participant_status || participant.status || 'active'} />
            {accessControl.can_edit && (
              <Select value={participant.participant_status || 'ACTIVE'} onValueChange={async v => {
                const isExit = v === 'EXITED';
                const result = await dangerConfirm({
                  title: isExit ? 'Exit participant?' : `Change status to ${v}?`,
                  description: isExit
                    ? 'The participant will be marked as exited. Provide an exit reason for audit.'
                    : 'Provide a reason so the audit trail captures why the status changed.',
                  confirmLabel: isExit ? 'Exit participant' : 'Change status',
                  reasonRequired: true,
                  variant: isExit ? 'danger' : 'warning',
                  testId: 'participant-status-change-confirm-2',
                });
                if (!result) return;
                try { await api.patch(`/participants/${participantId}/status`, { new_status: v, reason: result.reason || '', exit_reason: isExit ? 'OTHER' : undefined }); toast.success(`Status changed to ${v}`); fetchData(); }
                catch (e) { notify.error(e, 'PARTICIPANT_UPDATE_FAILED'); }
              }}>
                <SelectTrigger className="w-32 h-8 text-xs" data-testid="status-change"><SelectValue /></SelectTrigger>
                <SelectContent>{['ENQUIRY','ONBOARDING','ACTIVE','ON_HOLD','INACTIVE','EXITED'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}

      {/* Risk Alert Banner */}
      {riskAlerts?.has_alerts && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg" data-testid="risk-alert-banner">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700">{riskAlerts.count} Active Risk Alert{riskAlerts.count > 1 ? 's' : ''}</p>
            <div className="flex gap-2 mt-1 flex-wrap">
              {riskAlerts.alerts.map(a => (
                <Badge key={a.id} variant="destructive" className="text-xs">{a.severity}: {a.risk_type?.replace(/_/g, ' ')} — {a.description?.slice(0, 40)}</Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Support Snapshot — hidden when V3 is on (V3 has its own integrated snapshot) */}
      {showSnapshot && !profileV3Enabled && (
        <SupportSnapshotCard participantId={participantId} editFlagEnabled={snapshotEditFlagEnabled} intelligenceEnabled={snapshotIntelFlagEnabled} />
      )}

      {/* V3: Quick Actions only (KPIs shown in overview) */}
      {profileV3Enabled && (
        <div className="flex items-center justify-end gap-3 flex-wrap" data-testid="profile-v3-enhancements">
            <QuickActionBar participantId={participantId} accessControl={accessControl} canViewCharts={showChartsTab} />
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue={initialTab} data-testid="participant-tabs">
        {profileV3Enabled ? (
          /* V3: Simplified navigation */
          <TabsList className="flex flex-wrap h-auto gap-1" data-testid="tabs-v3">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="careplans" data-testid="tab-careplans">Care</TabsTrigger>
            {showDocumentsTab && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
            <TabsTrigger value="appointments" data-testid="tab-appointments">Schedule</TabsTrigger>
            {showChartsTab && <TabsTrigger value="charts" data-testid="tab-charts">Charts</TabsTrigger>}
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
            {accessControl.is_full_access && <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>}
            <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>
            <TabsTrigger value="medications" data-testid="tab-medications">Meds</TabsTrigger>
            {/* More tabs — less frequently used */}
            <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
            <TabsTrigger value="support-team" data-testid="tab-support-team">Team</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="tab-preferences">Preferences</TabsTrigger>
            {accessControl.can_view_ndis && <TabsTrigger value="ndis-details" data-testid="tab-ndis-details">NDIS</TabsTrigger>}
            {accessControl.can_view_funding && <TabsTrigger value="funding" data-testid="tab-funding">Funding</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="consents" data-testid="tab-consents">Consents</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring</TabsTrigger>}
            {accessControl.can_view_funding && <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="risks" data-testid="tab-risks">Risks</TabsTrigger>}
            <TabsTrigger value="continuity" data-testid="tab-continuity">Continuity</TabsTrigger>
            <TabsTrigger value="group-sessions" data-testid="tab-group-sessions">Groups</TabsTrigger>
            {accessControl.is_full_access && <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="teams" data-testid="tab-teams">Teams</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="duplicates" data-testid="tab-duplicates">Duplicates</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="linked-modules" data-testid="tab-linked-modules">Linked</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>}
            {accessControl.is_full_access && <TabsTrigger value="flags" data-testid="tab-flags">Flags</TabsTrigger>}
          </TabsList>
        ) : (
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
          {accessControl.can_view_ndis && (
            <TabsTrigger value="ndis-details" data-testid="tab-ndis-details">NDIS</TabsTrigger>
          )}
          {accessControl.can_view_funding && (
            <TabsTrigger value="funding" data-testid="tab-funding">Funding</TabsTrigger>
          )}
          <TabsTrigger value="support-team" data-testid="tab-support-team">Support Team</TabsTrigger>
          <TabsTrigger value="preferences" data-testid="tab-preferences">Preferences & Risks</TabsTrigger>
          {accessControl.is_full_access && (
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="consents" data-testid="tab-consents">Consents</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring Items</TabsTrigger>
          )}
          {accessControl.can_view_funding && (
            <TabsTrigger value="services" data-testid="tab-services">Service Items</TabsTrigger>
          )}
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="careplans" data-testid="tab-careplans">Care Plans</TabsTrigger>
          {showChartsTab && (
            <TabsTrigger value="charts" data-testid="tab-charts">Charts</TabsTrigger>
          )}
          {showDocumentsTab && (
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="risks" data-testid="tab-risks">Risk Assessments</TabsTrigger>
          )}
          <TabsTrigger value="goals" data-testid="tab-goals">Goals</TabsTrigger>
          <TabsTrigger value="medications" data-testid="tab-medications">Medications</TabsTrigger>
          <TabsTrigger value="continuity" data-testid="tab-continuity">Continuity</TabsTrigger>
          <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments</TabsTrigger>
          <TabsTrigger value="group-sessions" data-testid="tab-group-sessions">Group Sessions</TabsTrigger>
          {accessControl.is_full_access && (
            <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="teams" data-testid="tab-teams">Teams</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="duplicates" data-testid="tab-duplicates">Duplicates</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="linked-modules" data-testid="tab-linked-modules">Linked Modules</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="tags" data-testid="tab-tags">Tags</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="flags" data-testid="tab-flags">Flags</TabsTrigger>
          )}
          {accessControl.is_full_access && (
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          )}
        </TabsList>
        )}

        {/* V3: Inline Timeline Preview below Overview */}
        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {profileV3Enabled ? (
            <ParticipantProfileV3OverviewWrapper
              participant={participant}
              participantId={participantId}
              accessControl={accessControl}
              dashboard={dashboard}
              canEditOverview={overviewEditEnabled && (user?.permissions || []).includes('participants.manage')}
            />
          ) : overviewV2Enabled ? (
            <ParticipantOverviewV2
              participant={participant}
              participantId={participantId}
              accessControl={accessControl}
              dashboard={profileV3Enabled ? null : dashboard}
              editEnabled={overviewEditEnabled}
              onRefresh={fetchData}
            />
          ) : (
            <>
          {/* Dashboard KPI Cards */}
          {dashboard && (
            <div className={`grid grid-cols-2 ${accessControl.show_funding_amount ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3`} data-testid="participant-dashboard">
              {accessControl.show_funding_amount && (
                <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold text-green-600">${dashboard.funding_remaining?.toLocaleString()}</p><p className="text-[10px] text-muted-foreground">Funding Remaining</p></CardContent></Card>
              )}
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.active_shifts}</p><p className="text-[10px] text-muted-foreground">Active Shifts</p></CardContent></Card>
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.assigned_staff_count}</p><p className="text-[10px] text-muted-foreground">Assigned Staff</p></CardContent></Card>
              <Card className={dashboard.high_critical_risks > 0 ? 'border-red-200' : ''}><CardContent className="py-3 text-center"><p className={`text-xl font-bold ${dashboard.high_critical_risks > 0 ? 'text-red-600' : ''}`}>{dashboard.active_risks}</p><p className="text-[10px] text-muted-foreground">Active Risks</p></CardContent></Card>
              <Card><CardContent className="py-3 text-center"><p className="text-xl font-bold">{dashboard.upcoming_appointments}</p><p className="text-[10px] text-muted-foreground">Upcoming Appts</p></CardContent></Card>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {/* FRD Section 6.2 - Personal Details */}
                  <div><span className="text-muted-foreground">Full Name:</span><br/><span className="font-medium">{[participant.first_name, participant.middle_name, participant.last_name].filter(Boolean).join(' ') || '-'}</span></div>
                  <div><span className="text-muted-foreground">Preferred Name:</span><br/><span className="font-medium">{participant.preferred_name || '-'}</span></div>
                  <div><span className="text-muted-foreground">Date of Birth:</span><br/><span className="font-medium">{participant.date_of_birth || '-'}</span></div>
                  <div><span className="text-muted-foreground">Gender:</span><br/><span className="font-medium">{participant.gender || '-'}{participant.pronouns && <span className="text-muted-foreground ml-1">({participant.pronouns})</span>}</span></div>
                  <div><span className="text-muted-foreground">Phone:</span><br/><span className="font-medium">{participant.phone || '-'}</span></div>
                  {participant.alternate_phone && <div><span className="text-muted-foreground">Alt Phone:</span><br/><span className="font-medium">{participant.alternate_phone}</span></div>}
                  <div><span className="text-muted-foreground">Email:</span><br/><span className="font-medium">{participant.email || '-'}</span></div>
                  <div><span className="text-muted-foreground">Address:</span><br/><span className="font-medium">{participant.address || '-'}</span></div>
                  <div><span className="text-muted-foreground">State:</span><br/><span className="font-medium">{participant.state_id || '-'}</span></div>
                  <div><span className="text-muted-foreground">Branch:</span><br/><span className="font-mono">{participant.branch_id || '-'}</span></div>
                  {/* Enhanced fields - Only for permitted roles */}
                  {accessControl.can_view_funding && (
                    <div><span className="text-muted-foreground">Funding Type:</span><br/><Badge variant="outline">{participant.funding_type ? participant.funding_type.replace(/_/g, ' ') : 'Not Set'}</Badge></div>
                  )}
                  {accessControl.can_view_funding && participant.plan_start_date && <div><span className="text-muted-foreground">Plan Period:</span><br/><span className="font-medium">{participant.plan_start_date} — {participant.plan_end_date || 'Ongoing'}</span></div>}
                  {participant.guardian_name && <div><span className="text-muted-foreground">Guardian:</span><br/><span className="font-medium">{participant.guardian_name}</span>{participant.guardian_phone && <span className="text-xs text-muted-foreground ml-1">{participant.guardian_phone}</span>}</div>}
                  {participant.primary_language && <div><span className="text-muted-foreground">Language:</span><br/><span className="font-medium">{participant.primary_language}{participant.interpreter_required && <Badge className="ml-1 text-[10px]" variant="destructive">Interpreter Required</Badge>}</span></div>}
                  {participant.mobility_support_required && <div><Badge variant="outline" className="text-xs">Mobility Support Required</Badge></div>}
                  {participant.transport_support_required && <div><Badge variant="outline" className="text-xs">Transport Support Required</Badge></div>}
                  {participant.cultural_notes && <div className="col-span-2 md:col-span-3"><span className="text-muted-foreground">Cultural Notes:</span><br/><span className="text-sm">{participant.cultural_notes}</span></div>}
                </div>
              </CardContent>
            </Card>
            {/* Profile Photo */}
            {participant.profile_photo_url && (
              <Card className="lg:row-span-2">
                <CardContent className="p-4 flex justify-center">
                  <img 
                    src={participant.profile_photo_url} 
                    alt={`${participant.first_name}'s profile`}
                    className="w-32 h-32 rounded-full object-cover border-2"
                  />
                </CardContent>
              </Card>
            )}
            {!participant.profile_photo_url && (
              <AISummaryPanel 
                summaryType="PARTICIPANT" 
                entityId={participantId}
                entityType="PARTICIPANT"
                title="AI Summary"
              />
            )}
          </div>
          {participant.profile_photo_url && (
            <AISummaryPanel 
              summaryType="PARTICIPANT" 
              entityId={participantId}
              entityType="PARTICIPANT"
              title="AI Summary"
            />
          )}
            </>
          )}
          {/* V3: Inline Timeline Preview */}
          {profileV3Enabled && timelineV2Enabled && (
            <InlineTimelinePreview participantId={participantId} />
          )}
        </TabsContent>

        {/* ADDRESSES TAB (NEW) */}
        <TabsContent value="addresses" className="space-y-4 mt-4">
          <AddressesTab participantId={participantId} />
        </TabsContent>

        {/* NDIS DETAILS TAB (NEW) */}
        <TabsContent value="ndis-details" className="space-y-4 mt-4">
          <NDISFundingDetailsTab participantId={participantId} />
        </TabsContent>

        {/* FUNDING TAB */}
        <TabsContent value="funding" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Funding Plans</h2>
            <Dialog open={fundingDialog} onOpenChange={setFundingDialog}>
              <DialogTrigger asChild><Button size="sm" data-testid="create-funding-button"><Plus className="h-4 w-4 mr-1" /> New Plan</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create Funding Plan</DialogTitle><DialogDescription>Add a funding plan with NDIS categories</DialogDescription></DialogHeader>
                <form onSubmit={createFundingPlan} className="space-y-4">
                  <div className="space-y-2"><Label>Plan Name</Label><Input value={fpForm.plan_name} onChange={(e) => setFpForm({...fpForm, plan_name: e.target.value})} required data-testid="fp-name-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={fpForm.start_date} onChange={(e) => setFpForm({...fpForm, start_date: e.target.value})} required /></div>
                    <div className="space-y-2"><Label>End Date</Label><Input type="date" value={fpForm.end_date} onChange={(e) => setFpForm({...fpForm, end_date: e.target.value})} required /></div>
                  </div>
                  <div className="border rounded-lg p-3 space-y-3">
                    <Label className="font-medium">Budget Categories</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1"><Label className="text-xs">CORE ($)</Label><Input type="number" step="0.01" value={fpForm.core} onChange={(e) => setFpForm({...fpForm, core: e.target.value})} placeholder="0" data-testid="fp-core-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">CAPACITY ($)</Label><Input type="number" step="0.01" value={fpForm.capacity} onChange={(e) => setFpForm({...fpForm, capacity: e.target.value})} placeholder="0" /></div>
                      <div className="space-y-1"><Label className="text-xs">CAPITAL ($)</Label><Input type="number" step="0.01" value={fpForm.capital} onChange={(e) => setFpForm({...fpForm, capital: e.target.value})} placeholder="0" /></div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="fp-submit-button">Create Plan</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Plans + Balances */}
          {fundingPlans.map(fp => (
            <Card key={fp.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4" /> {fp.plan_name}</CardTitle>
                <p className="text-xs text-muted-foreground">{fp.start_date} to {fp.end_date}</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="font-bold tabular-nums">${(fp.total_budget||0).toLocaleString()}</p>
                  </div>
                  {(fp.categories||[]).map(c => (
                    <div key={c.type} className="bg-muted/50 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">{c.type}</p>
                      <p className="font-bold tabular-nums text-sm">${(c.remaining||0).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">of ${(c.allocated||0).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Ledger Table */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Transaction Ledger</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {transactions.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No transactions</TableCell></TableRow>
                  : transactions.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">{t.created_at?.split('T')[0]}</TableCell>
                      <TableCell className="text-sm">{t.description}</TableCell>
                      <TableCell><StatusBadge status={t.category || 'CORE'} /></TableCell>
                      <TableCell className="text-right font-mono tabular-nums text-destructive">-${(t.amount||0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUPPORT TEAM TAB (NEW) */}
        <TabsContent value="support-team" className="space-y-4 mt-4">
          <SupportTeamTab participantId={participantId} />
        </TabsContent>

        {/* PREFERENCES & RISKS TAB (NEW) */}
        <TabsContent value="preferences" className="space-y-4 mt-4">
          <PreferencesRisksTab participantId={participantId} />
        </TabsContent>

        {/* CONSENTS TAB (NEW - Phase 5) */}
        <TabsContent value="consents" className="space-y-4 mt-4">
          <ConsentsTab participantId={participantId} />
        </TabsContent>

        {/* EXPIRING ITEMS TAB (NEW - Phase 5) */}
        <TabsContent value="expiring" className="space-y-4 mt-4">
          <ExpiringItemsTab participantId={participantId} />
        </TabsContent>

        {/* SERVICE ITEMS TAB */}
        <TabsContent value="services" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">Service Items</h2>
            <Dialog open={itemDialog} onOpenChange={setItemDialog}>
              <DialogTrigger asChild><Button size="sm" data-testid="create-service-item-button"><Plus className="h-4 w-4 mr-1" /> Add Service Item</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add Service Item</DialogTitle><DialogDescription>Create a recurring service schedule</DialogDescription></DialogHeader>
                <form onSubmit={createServiceItem} className="space-y-4">
                  <div className="space-y-2"><Label>Service</Label>
                    <Select value={siForm.service_catalog_id} onValueChange={(v) => setSiForm({...siForm, service_catalog_id: v})}>
                      <SelectTrigger data-testid="si-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                      <SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} (${s.rate_per_unit}/h)</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Funding Plan</Label>
                    <Select value={siForm.funding_plan_id} onValueChange={(v) => setSiForm({...siForm, funding_plan_id: v})}>
                      <SelectTrigger data-testid="si-plan-select"><SelectValue placeholder="Select plan" /></SelectTrigger>
                      <SelectContent>{fundingPlans.map(f => <SelectItem key={f.id} value={f.id}>{f.plan_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2"><Label>Day</Label>
                      <Select value={siForm.day_of_week} onValueChange={(v) => setSiForm({...siForm, day_of_week: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{dayNames.map((d,i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Start</Label><Input type="time" value={siForm.start_time} onChange={(e) => setSiForm({...siForm, start_time: e.target.value})} /></div>
                    <div className="space-y-2"><Label>End</Label><Input type="time" value={siForm.end_time} onChange={(e) => setSiForm({...siForm, end_time: e.target.value})} /></div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="si-submit-button">Add Service Item</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Day</TableHead><TableHead>Time</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {serviceItems.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No service items</TableCell></TableRow>
                  : serviceItems.map(si => (
                    <TableRow key={si.id}>
                      <TableCell className="font-medium">{si.service_name || 'N/A'}</TableCell>
                      <TableCell>{dayNames[si.day_of_week] || 'N/A'}</TableCell>
                      <TableCell className="font-mono text-xs tabular-nums">{si.start_time} - {si.end_time}</TableCell>
                      <TableCell>
                        {si.last_generated_at ? (
                          <div className="space-y-0.5">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px]" data-testid={`generated-badge-${si.id}`}>
                              <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" />Generated
                            </Badge>
                            <p className="text-[10px] text-muted-foreground">{si.last_generated_period_start} — {si.last_generated_period_end}</p>
                            <p className="text-[10px] text-muted-foreground">{si.total_shifts_generated || si.last_generated_count} shift(s)</p>
                          </div>
                        ) : (
                          <span className="text-[10px] text-muted-foreground">Not generated</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => generateShifts(si.id)} disabled={generating === si.id} data-testid={`generate-shifts-${si.id}`}>
                          <Zap className="h-3 w-3 mr-1" /> {generating === si.id ? 'Generating...' : si.last_generated_at ? 'Extend 4 Weeks' : 'Generate 4 Weeks'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTES TAB */}
        <TabsContent value="notes" className="space-y-4 mt-4">
          <NotesTab participantId={participantId} />
        </TabsContent>

        {/* CARE PLANS TAB */}
        <TabsContent value="careplans" className="space-y-4 mt-4">
          <CarePlansTab participantId={participantId} />
        </TabsContent>

        {/* CHARTS TAB (Day 1 foundation, feature-flag gated) */}
        {showChartsTab && (
          <TabsContent value="charts" className="space-y-4 mt-4">
            <ParticipantChartsTab participantId={participantId} />
          </TabsContent>
        )}

        {/* DOCUMENTS TAB (Day 2 Participant Documents, feature-flag gated) */}
        {showDocumentsTab && (
          <TabsContent value="documents" className="space-y-4 mt-4">
            <ParticipantDocumentsTab participantId={participantId} />
          </TabsContent>
        )}

        {/* RISKS TAB */}
        <TabsContent value="risks" className="space-y-4 mt-4">
          <RisksTab participantId={participantId} />
        </TabsContent>

        {/* GOALS TAB */}
        <TabsContent value="goals" className="space-y-4 mt-4">
          <GoalsTab participantId={participantId} />
        </TabsContent>


        {/* MEDICATIONS TAB */}
        <TabsContent value="medications" className="space-y-4 mt-4">
          <MedicationsTab participantId={participantId} />
        </TabsContent>

        {/* CONTINUITY TAB */}
        <TabsContent value="continuity" className="space-y-4 mt-4">
          <ContinuityTab participantId={participantId} />
        </TabsContent>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <ContactsTab participantId={participantId} />
        </TabsContent>

        {/* APPOINTMENTS TAB */}
        <TabsContent value="appointments" className="space-y-4 mt-4">
          <AppointmentsTab participantId={participantId} />
        </TabsContent>

        {/* GROUP SESSIONS TAB (Day 4) */}
        <TabsContent value="group-sessions" className="space-y-4 mt-4">
          <GroupSessionsTab participantId={participantId} />
        </TabsContent>

        {/* FORMS TAB */}
        <TabsContent value="forms" className="space-y-4 mt-4">
          <FormsTab participantId={participantId} />
        </TabsContent>

        {/* TEAMS TAB */}
        <TabsContent value="teams" className="space-y-4 mt-4">
          <TeamsTab participantId={participantId} />
        </TabsContent>

        {/* DUPLICATES TAB (NEW - Phase 6) */}
        <TabsContent value="duplicates" className="space-y-4 mt-4">
          <DuplicateDetectionTab participantId={participantId} />
        </TabsContent>

        {/* LINKED MODULES TAB (NEW - FRD Section 6.12) */}
        <TabsContent value="linked-modules" className="space-y-4 mt-4">
          <LinkedModulesTab participantId={participantId} />
        </TabsContent>

        {/* TAGS TAB (NEW - FRD Section 10.11) */}
        <TabsContent value="tags" className="space-y-4 mt-4">
          <TagsTab participantId={participantId} />
        </TabsContent>

        {/* FLAGS TAB (NEW - FRD Section 10.5) */}
        <TabsContent value="flags" className="space-y-4 mt-4">
          <FlagsTab participantId={participantId} />
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline" className="space-y-4 mt-4">
          {timelineV2Enabled ? (
            <ParticipantUnifiedTimeline participantId={participantId} />
          ) : (
            <TimelineEventsTab participantId={participantId} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
